import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import { List } from 'immutable';
import PropTypes from 'prop-types';

import { getActiveTipPolicyId } from 'selectors/session';

import Section from 'features/timesheets/TimesheetsPage/TimecardModal/Section';
import {
  EVENT_ACTIONS,
  trackTimecardClick,
} from 'features/timesheets/tracking';

import { cxHelpers } from 'util/className';
import { formatDate } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import Notes from '../ManagerNoteSection/Notes';

import CashTips from './CashTips';
import CreditTips from './CreditTips';
import TipOut from './TipOut';

const { cx, cxEl } = cxHelpers('TipsSection');

const MAX_TIP = 999999;

const TipsSection = ({
  cashTips,
  cashTipOutEligible,
  creditTips,
  tipOut,
  creditTipOutEligible,
  tipOutUpdatedAt,
  onChange,
  onChangeFormError,
  setChangesMade,
  shift,
  activeTipPolicyIdExists,
}) => {
  const tipNotes = (shift.get('notes') || List()).filter(
    note => note.get('event_type') === 'cash_tips'
  );
  const isInvalidValue = value => value && value > MAX_TIP;

  const handleChangeCashTips = useCallback(
    e => {
      onChange(
        'cash_tips',
        e.target.value !== '' ? parseFloat(e.target.value) : null
      );
      onChangeFormError(
        'cash_tips',
        isInvalidValue(parseFloat(e.target.value)) &&
          toI18n('timecard_modal_v3.tips_edit_section.error_exceeded_max_tips')
      );
      setChangesMade(true);
    },
    [onChange, onChangeFormError, setChangesMade]
  );
  const handleChangeCreditTips = useCallback(
    e => {
      onChange(
        'credit_tips',
        e.target.value !== '' ? parseFloat(e.target.value) : null
      );
      onChangeFormError(
        'credit_tips',
        isInvalidValue(parseFloat(e.target.value)) &&
          toI18n('timecard_modal_v3.tips_edit_section.error_exceeded_max_tips')
      );
      setChangesMade(true);
    },
    [onChange, onChangeFormError, setChangesMade]
  );

  const trackTipsClick = useCallback(tipsType => {
    const eventAction = {
      cash_tips: EVENT_ACTIONS.CASH_TIPS_CLICKED,
      credit_tips: EVENT_ACTIONS.CREDIT_TIPS_CLICKED,
    };
    trackTimecardClick({ eventAction: eventAction[tipsType] });
  }, []);

  return (
    <Box className={cx()}>
      <Section
        className={cxEl('tips')}
        iconName="Money"
        title={toI18n('timecard_modal_v3.tips_edit_section.title')}
        collapsedContent={
          tipNotes.count() > 0 && <Notes notes={tipNotes} type="tips_notes" />
        }
      />
      <Section
        className={cxEl('paid-tips-input')}
        expandedContent={null}
        bottomBorder
        bottomPadding
        collapsedContent={
          <Box column gap={16}>
            {(!tipOut || creditTipOutEligible) && activeTipPolicyIdExists && (
              <Box mt={-8}>
                <Text
                  variant="captions"
                  color="mono700"
                  i18n="timecard_modal_v3.tips_edit_section.tip_pooling_message"
                />
              </Box>
            )}
            <Box row gap={24}>
              {((cashTipOutEligible && tipOut == null) ||
                !cashTipOutEligible) && (
                <CashTips
                  cashTips={cashTips}
                  cashTipOutEligible={cashTipOutEligible}
                  handleChangeCashTips={handleChangeCashTips}
                  trackTipsClick={trackTipsClick}
                />
              )}
              {tipOut == null && (
                <CreditTips
                  creditTips={creditTips}
                  creditTipOutEligible={creditTipOutEligible}
                  trackTipsClick={trackTipsClick}
                  handleChangeCreditTips={handleChangeCreditTips}
                />
              )}
              {tipOut !== null && tipOut !== undefined && (
                <TipOut tipOut={tipOut} />
              )}
            </Box>
            {tipOutUpdatedAt && (
              <Text
                variant="bodySm"
                fs={14}
                color="mono700"
                i18n="timecard_modal_v3.tips_section.tip_out_updated_at"
                i18nProps={{
                  date: formatDate(tipOutUpdatedAt, 'trade_time'),
                }}
              />
            )}
          </Box>
        }
      />
    </Box>
  );
};

TipsSection.propTypes = {
  cashTips: PropTypes.number,
  cashTipOutEligible: PropTypes.bool,
  creditTips: PropTypes.number,
  tipOut: PropTypes.number,
  creditTipOutEligible: PropTypes.bool,
  tipOutUpdatedAt: PropTypes.string,
  onChange: PropTypes.func,
  activeTipPolicyIdExists: PropTypes.bool.isRequired,
};

export default connect(state => ({
  activeTipPolicyIdExists: getActiveTipPolicyId(state),
}))(TipsSection);
